import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { enqueue, isOfflineError } from '../utils/offlineStore';
import { ArrowLeft, Save, Upload, X, Droplets, Wheat, Droplet, Bug, AlertTriangle, CheckCircle2, Activity, Calendar, Cloud, Eye, Image, Home, Bell, Camera, Video, UserCircle, Mic, Scan, Zap, TrendingUp, ExternalLink, Lightbulb, Plus, Scale, TrendingDown, ArrowUpRight, ArrowDownRight, Box, Layers } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Toggle } from '../components/ui/toggle';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import VoiceInspection from '../components/VoiceInspection';
import FrameScanDialog from '../components/FrameScanDialog';
import TreatmentPlanWizard from '../components/TreatmentPlanWizard';
import MiteCountDialog from '../components/MiteCountDialog';
import { uploadFileChunked } from '../utils/chunkedUpload';
import { Section, FormField, InfoButton, DISEASE_INFO, PEST_INFO } from '../components/inspection/InspectionShared';
import PhotosVideosSection from '../components/inspection/PhotosVideosSection';
import TasksSection from '../components/inspection/TasksSection';
import HeaderSection from '../components/inspection/HeaderSection';
import OverviewSection from '../components/inspection/OverviewSection';
import WeightSection from '../components/inspection/WeightSection';
import FrameByFrameSection from '../components/inspection/FrameByFrameSection';
import ConditionSection from '../components/inspection/ConditionSection';
import ResourcesSection from '../components/inspection/ResourcesSection';
import MiteSection from '../components/inspection/MiteSection';
import ActionsSection from '../components/inspection/ActionsSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InspectionForm = ({ editMode = false }) => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const hiveId = params.hiveId || searchParams.get('hiveId');
  const inspectionId = params.inspectionId || searchParams.get('inspectionId');
  const navigate = useNavigate();
  const { token: contextToken, isCrew: contextIsCrew, user } = useAuth();
  // Use context token or fall back to localStorage (fixes crew navigation race condition)
  const token = contextToken || localStorage.getItem('crewToken') || localStorage.getItem('token');
  const isCrew = contextIsCrew || !!localStorage.getItem('crewToken');
  const [hive, setHive] = useState(null);
  const [beekeepers, setBeekeepers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [blobUrls, setBlobUrls] = useState({});
  const offlinePhotosRef = React.useRef({});
  const [uploadLimits, setUploadLimits] = useState({ max_photos: 5, max_videos: 0 });
  const [videoUploadProgress, setVideoUploadProgress] = useState(null); // {percent, status}
  const [showVoiceDialog, setShowVoiceDialog] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  
  // Mite feature states
  const [showFrameScanDialog, setShowFrameScanDialog] = useState(false);
  const [showTreatmentWizard, setShowTreatmentWizard] = useState(false);
  const [showMiteCountDialog, setShowMiteCountDialog] = useState(false);
  const [treatmentTypes, setTreatmentTypes] = useState([]);
  const [recentMiteCounts, setRecentMiteCounts] = useState([]);
  const [activeTreatments, setActiveTreatments] = useState([]);
  const [treatmentContext, setTreatmentContext] = useState(null);
  const [treatmentJustStarted, setTreatmentJustStarted] = useState(null);
  const [allHives, setAllHives] = useState([]);
  const [allYards, setAllYards] = useState([]);
  
  // Dynamic task types and action types
  const [availableTaskTypes, setAvailableTaskTypes] = useState([]);
  const [taskCategories, setTaskCategories] = useState([]);
  const [availableActionTypes, setAvailableActionTypes] = useState([]);
  const [actionCategories, setActionCategories] = useState([]);
  
  // Suggest new action/task state
  const [showSuggestAction, setShowSuggestAction] = useState(false);
  const [showSuggestTask, setShowSuggestTask] = useState(false);
  const [suggestActionName, setSuggestActionName] = useState('');
  const [suggestActionCategory, setSuggestActionCategory] = useState('other');
  const [suggestTaskName, setSuggestTaskName] = useState('');
  const [suggestTaskCategory, setSuggestTaskCategory] = useState('other');
  const [submittingSuggestion, setSubmittingSuggestion] = useState(false);

  // Hive Weight tracking state
  const [weightSettings, setWeightSettings] = useState({ unit_preference: 'kg', alert_threshold_percent: 10 });
  const [baselineWeight, setBaselineWeight] = useState(null);
  const [weightHistory, setWeightHistory] = useState([]);
  const [previousWeight, setPreviousWeight] = useState(null);

  // Weather auto-fetch state
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherAutoFetched, setWeatherAutoFetched] = useState(false);

  // Enhanced special actions state
  const [availableQueens, setAvailableQueens] = useState([]);
  const [emptyHives, setEmptyHives] = useState([]);

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    beekeeper: '',
    time: new Date().toTimeString().slice(0, 5), // HH:MM format for time input
    temperature: '',
    humidity: '',
    wind: '',
    hive_weight: '',
    baseline_weight: '',
    weight_loss_reasons: [],
    weather: [],
    inspection_type: [],
    hive_stack: {},
    activity_entrance: [],
    drone_population: '',
    hive_condition: '',
    repairs_needed: '',
    colony_mood: '',
    hive_orientation: '',
    bottom_brood_box: Array(10).fill(''),
    top_brood_box: Array(10).fill(''),
    brood: [],
    pattern: [],
    queen_cells_frames: [],
    capped_cells_frames: [],
    queen_spotted: false,
    queen_frame: '',
    queen_marked: false,
    queen_color: '',
    queen_age: '',
    queen_cells: false,
    queen_cells_frame: '',
    capped_cells: false,
    capped_cells_frame: '',
    no_queen: false,
    foul_odour: false,
    population: '',
    swarm_conditions: false,
    hive_condition_status: [],
    diseases: [],
    pests: [],
    pests_other: '',
    nectar_level: '',
    pollen_level: '',
    honey_brood_level: '',
    honey_frames: [],
    honey_frames_count: '',
    feeding: '',
    feed_type: [],
    feeder_type: '',
    mite_wash: false,
    mite_method: '',
    mite_count: '',
    mite_percentage: '',
    mite_treatment: [],
    mite_application: [],
    mite_dosage: '',
    actions: [],
    actions_other: '',
    actions_to_take: [],
    actions_assigned_beekeeper: '', // Beekeeper assigned to future actions
    // Split Hive details
    split_removed_brood_frame: '',
    split_removed_pollen_frame: '',
    split_removed_honey_frame: '',
    split_destination: '', // 'existing' or 'new'
    split_destination_hive_id: '',
    split_new_hive_yard: '',
    // Requeened details
    requeened_date: '',
    requeened_source: '', // 'queen_batch', 'purchased', 'emergency_cell', 'other'
    requeened_queen_cell_id: '',
    requeened_batch_id: '',
    requeened_queen_breed: '',
    requeened_notes: '',
    // Dead Out / Lost Colony
    dead_out_cause: [],
    dead_out_notes: '',
    // Super/Pack details
    added_super_boxes: '',
    removed_super_boxes: '',
    winter_pack_boxes: '',
    // Requeened details
    requeened_date: '',
    added_brood_box_qty: '',
    removed_brood_box_qty: '',
    added_super_qty: '',
    removed_super_qty: '',
    added_frames_qty: '',
    removed_frames_qty: '',
    // Harvest tracking (Full Super & Capped Honey Frames)
    added_full_super_qty: '',
    removed_full_super_qty: '',
    added_capped_honey_frames_qty: '',
    removed_capped_honey_frames_qty: '',
    added_nutrition: false,
    nutrition_details: '',
    // Combined Hives details
    combine_primary_hive: '', // The hive receiving the bees
    combine_source_hive: '', // The hive being combined into primary
    combine_empty_hive_action: '', // What to do with empty hive: 'inactive', 'storage', 'disassemble', 'other'
    combine_empty_hive_notes: '',
    // Moved Hive details
    moved_hive_type: '', // 'same_apiary', 'different_apiary', 'new_location'
    moved_hive_reason: '', // 'better_sun', 'flooding', 'neighbor_complaint', 'expansion', 'consolidation', 'other'
    moved_hive_new_position: '', // Position description within apiary
    moved_hive_new_apiary: '', // If moved to different apiary
    moved_hive_notes: '',
    notes: '',
    photos: [],
    videos: [],
  });

  const calculateMitePercentage = (count) => {
    if (!count || count === '') return '';
    const percentage = (parseFloat(count) / 300 * 100).toFixed(2);
    return percentage;
  };

  const handleMiteCountChange = (count) => {
    setFormData({
      ...formData,
      mite_count: count,
      mite_percentage: calculateMitePercentage(count)
    });
  };

  useEffect(() => {
    if (token) {
      fetchHive();
      if (editMode && inspectionId) {
        fetchInspection();
      }
    }
  }, [hiveId, inspectionId, editMode, token]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(blobUrls).forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  // Get location for weather data — PRIORITIZE yard GPS (apiary location) over user browser location
  useEffect(() => {
    if (editMode) return;
    
    // First, try to use the yard's GPS (where the hive actually is)
    const tryYardLocation = () => {
      if (allYards.length > 0 && hive) {
        let yard = null;
        if (hive.yard_id) {
          yard = allYards.find(y => y.id === hive.yard_id);
        } else if (hive.yard) {
          yard = allYards.find(y => y.name === hive.yard || y.name?.trim() === hive.yard);
        }
        if (yard?.gps_lat && yard?.gps_lng) {
          setUserLocation({
            lat: yard.gps_lat,
            lng: yard.gps_lng,
            source: 'yard'
          });
          return true;
        }
      }
      return false;
    };

    // If yard GPS is available, use it; otherwise fall back to browser geolocation
    if (!tryYardLocation()) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setUserLocation({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
              source: 'user'
            });
          },
          () => {} // Location denied and no yard GPS — no weather auto-fill
        );
      }
    }
  }, [editMode, hive?.yard_id, hive?.yard, allYards]);

  // Auto-fetch weather when location is available (only for new inspections)
  useEffect(() => {
    const fetchWeather = async () => {
      if (!userLocation || editMode || weatherAutoFetched) return;
      
      // Only auto-populate if weather fields are empty
      if (formData.temperature || formData.humidity || formData.wind || formData.weather.length > 0) {
        return;
      }
      
      setWeatherLoading(true);
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(
          `${API}/weather/current?lat=${userLocation.lat}&lng=${userLocation.lng}`,
          { headers }
        );
        
        const weather = response.data;
        if (weather && !weather.error) {
          // Map conditions to weather toggle options
          const mapConditionsToWeather = (conditions, weatherCode) => {
            const weatherOptions = [];
            if (!conditions) return weatherOptions;
            
            const lower = conditions.toLowerCase();
            
            // Map based on weather code ranges and description
            if (weatherCode === 0 || lower.includes('clear')) {
              weatherOptions.push('Sunny');
            }
            if (weatherCode >= 1 && weatherCode <= 2) {
              weatherOptions.push('Partly Cloudy');
            }
            if (weatherCode === 3 || lower.includes('overcast')) {
              weatherOptions.push('Cloudy');
            }
            if ((weatherCode >= 51 && weatherCode <= 55) || (weatherCode >= 61 && weatherCode <= 63) || (weatherCode >= 80 && weatherCode <= 81)) {
              weatherOptions.push('Light Rain');
            }
            if ((weatherCode >= 65 && weatherCode <= 67) || weatherCode === 82) {
              weatherOptions.push('Heavy Rain');
            }
            if (weatherCode < 51 && !lower.includes('rain') && !lower.includes('drizzle') && !lower.includes('snow')) {
              weatherOptions.push('Dry');
            }
            
            return weatherOptions;
          };
          
          const weatherConditions = mapConditionsToWeather(weather.conditions, weather.weather_code);
          
          setFormData(prev => ({
            ...prev,
            temperature: weather.temperature ? `${weather.temperature}°C` : prev.temperature,
            humidity: weather.humidity ? `${weather.humidity}%` : prev.humidity,
            wind: weather.wind_speed ? `${weather.wind_speed} km/h` : prev.wind,
            weather: weatherConditions.length > 0 ? weatherConditions : prev.weather
          }));
          
          setWeatherAutoFetched(true);
          const locationSource = userLocation.source === 'yard' 
            ? `apiary location${hive?.yard ? ` (${hive.yard})` : ''}`
            : 'your current location';
          toast.success(`Weather auto-filled from ${locationSource}`, { duration: 3000 });
        }
      } catch (error) {
      } finally {
        setWeatherLoading(false);
      }
    };
    
    fetchWeather();
  }, [userLocation, editMode, weatherAutoFetched, token, formData.temperature, formData.humidity, formData.wind, formData.weather.length]);

  // Handle voice-parsed data
  const handleVoiceDataParsed = (parsedData) => {
    
    // Helper to convert values to Title Case
    const toTitleCase = (str) => {
      if (!str) return str;
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };
    
    // Map inspection type
    const mapInspectionType = (arr) => {
      if (!arr) return [];
      const items = Array.isArray(arr) ? arr : [arr];
      return items.map(item => {
        const lower = item.toLowerCase();
        if (lower.includes('external') || lower.includes('outside') || lower.includes('quick')) return 'External';
        if (lower.includes('superficial') || lower.includes('brief')) return 'Superficial';
        if (lower.includes('extensive') || lower.includes('full') || lower.includes('thorough') || lower.includes('complete')) return 'Extensive';
        if (lower.includes('weekly')) return 'Weekly';
        if (lower.includes('monthly')) return 'Monthly';
        if (lower.includes('bi-monthly') || lower.includes('bimonthly')) return 'Bi-Monthly';
        return toTitleCase(item);
      }).filter(Boolean);
    };
    
    // Map activity at entrance
    const mapActivityEntrance = (arr) => {
      if (!arr) return [];
      const items = Array.isArray(arr) ? arr : [arr];
      return items.map(item => {
        const lower = item.toLowerCase();
        if (lower.includes('none') || lower.includes('no activity') || lower.includes('quiet')) return 'None';
        if (lower.includes('low') || lower.includes('little') || lower.includes('minimal')) return 'Low';
        if (lower.includes('average') || lower.includes('normal') || lower.includes('moderate')) return 'Average';
        if (lower.includes('high') || lower.includes('busy') || lower.includes('lots')) return 'High';
        if (lower.includes('beard')) return 'Bearding';
        if (lower.includes('pollen')) return 'Pollen';
        return toTitleCase(item);
      }).filter(Boolean);
    };
    
    // Map AI values to form values
    const mapPopulation = (val) => {
      if (!val) return '';
      const lower = val.toLowerCase();
      if (lower.includes('weak') || lower.includes('low') || lower.includes('sparse')) return 'Low';
      if (lower.includes('moderate') || lower.includes('average') || lower.includes('medium')) return 'Average';
      if (lower.includes('strong') || lower.includes('high') || lower.includes('very')) return 'High';
      if (lower.includes('none') || lower.includes('empty')) return 'None';
      return toTitleCase(val);
    };
    
    const mapColonyMood = (val) => {
      if (!val) return '';
      const lower = val.toLowerCase();
      if (lower.includes('calm') || lower.includes('gentle') || lower.includes('docile')) return 'Calm';
      if (lower.includes('nervous') || lower.includes('flighty') || lower.includes('anxious')) return 'Nervous';
      if (lower.includes('aggressive') || lower.includes('defensive') || lower.includes('angry')) return 'Defensive';
      if (lower.includes('buzz')) return 'Buzzing';
      if (lower.includes('beard')) return 'Bearding';
      return toTitleCase(val);
    };
    
    const mapHiveCondition = (val) => {
      if (!val) return '';
      const lower = val.toLowerCase();
      if (lower.includes('good') || lower.includes('excellent') || lower.includes('great')) return 'Good';
      if (lower.includes('fair') || lower.includes('ok') || lower.includes('okay') || lower.includes('average')) return 'Fair';
      if (lower.includes('poor') || lower.includes('bad') || lower.includes('weak')) return 'Poor';
      if (lower.includes('damage') || lower.includes('broken')) return 'Damaged';
      return toTitleCase(val);
    };
    
    const mapBrood = (arr) => {
      if (!arr) return [];
      const items = Array.isArray(arr) ? arr : [arr];
      return items.map(item => {
        const lower = item.toLowerCase();
        if (lower.includes('egg')) return 'Eggs';
        if (lower.includes('larva')) return 'Larvae';
        if (lower.includes('capped') || lower.includes('sealed')) return 'Capped';
        if (lower.includes('drone')) return 'Drone';
        if (lower.includes('bias') || lower.includes('all')) return 'BIAS';
        if (lower.includes('none')) return 'None';
        return toTitleCase(item);
      }).filter(Boolean);
    };
    
    const mapPattern = (arr) => {
      if (!arr) return [];
      const items = Array.isArray(arr) ? arr : [arr];
      return items.map(item => {
        const lower = item.toLowerCase();
        if (lower.includes('solid') || lower.includes('good') || lower.includes('excellent')) return 'Solid';
        if (lower.includes('partly') || lower.includes('partial')) return 'Partly Spotty';
        if (lower.includes('spotty') || lower.includes('scattered') || lower.includes('irregular')) return 'Spotty';
        if (lower.includes('drone layer')) return 'Drone Layer';
        if (lower.includes('multiple egg')) return 'Multiple Eggs per Cell';
        return null;
      }).filter(Boolean);
    };
    
    const mapDiseases = (arr) => {
      if (!arr) return [];
      const items = Array.isArray(arr) ? arr : [arr];
      return items.map(item => {
        const lower = item.toLowerCase();
        if (lower.includes('sacbrood')) return 'Sacbrood';
        if (lower.includes('dwv') || lower.includes('deformed wing')) return 'DWV';
        if (lower.includes('efb') || lower.includes('european foul')) return 'EFB';
        if (lower.includes('afb') || lower.includes('american foul')) return 'AFB';
        if (lower.includes('chalk')) return 'Chalkbrood';
        if (lower.includes('bpms') || lower.includes('paralysis')) return 'BPMS';
        if (lower.includes('nosema')) return 'Nosema';
        return null;
      }).filter(Boolean);
    };
    
    const mapPests = (arr) => {
      if (!arr) return [];
      const items = Array.isArray(arr) ? arr : [arr];
      return items.map(item => {
        const lower = item.toLowerCase();
        if (lower.includes('ant')) return 'Ants';
        if (lower.includes('mite') || lower.includes('varroa')) return 'Mites';
        if (lower.includes('mice') || lower.includes('mouse')) return 'Mice';
        if (lower.includes('wax moth') || lower.includes('moth')) return 'Wax Moths';
        if (lower.includes('shb') || lower.includes('small hive') || lower.includes('beetle')) return 'SHB';
        if (lower.includes('wasp')) return 'Wasps';
        return null;
      }).filter(Boolean);
    };
    
    const mapLevel = (val) => {
      if (!val) return '';
      const lower = val.toLowerCase();
      if (lower.includes('none') || lower.includes('empty') || lower === 'l' || lower === 'low') return 'L';
      if (lower.includes('light') || lower.includes('moderate') || lower.includes('medium') || lower === 'm') return 'M';
      if (lower.includes('heavy') || lower.includes('high') || lower.includes('full') || lower === 'h') return 'H';
      return val.charAt(0).toUpperCase();
    };
    
    // Map actions taken
    const mapActionsTaken = (arr) => {
      if (!arr) return [];
      const items = Array.isArray(arr) ? arr : [arr];
      return items.map(item => {
        const lower = item.toLowerCase();
        if (lower.includes('split')) return 'Split Hive';
        if (lower.includes('requeen') || lower.includes('re-queen') || lower.includes('new queen')) return 'Requeened';
        if (lower.includes('winter') || lower.includes('pack down')) return 'Winter Pack Down';
        if (lower.includes('added super') || lower.includes('add super') || lower.includes('put on super')) return 'Added Super';
        if (lower.includes('removed super') || lower.includes('remove super') || lower.includes('took off super')) return 'Removed Super';
        if (lower.includes('added frame') || lower.includes('add frame')) return 'Added Frames';
        if (lower.includes('removed frame') || lower.includes('remove frame') || lower.includes('pulled frame')) return 'Removed Frames';
        if (lower.includes('added feeder') || lower.includes('add feeder') || lower.includes('put feeder')) return 'Added Feeder';
        if (lower.includes('removed feeder') || lower.includes('remove feeder')) return 'Removed Feeder';
        if (lower.includes('harvest') || lower.includes('extracted') || lower.includes('pulled honey')) return 'Removed Capped Honey Frames';
        if (lower.includes('added brood') || lower.includes('add brood box')) return 'Added Brood Box';
        if (lower.includes('removed brood')) return 'Removed Brood Box';
        return null;
      }).filter(Boolean);
    };
    
    // Map actions to take (future)
    const mapActionsToTake = (arr) => {
      if (!arr) return [];
      const items = Array.isArray(arr) ? arr : [arr];
      return items.map(item => {
        const lower = item.toLowerCase();
        if (lower.includes('mite wash') || lower.includes('mite test') || lower.includes('mite count')) return 'Mite Wash';
        if (lower.includes('split')) return 'Split Hive';
        if (lower.includes('requeen') || lower.includes('re-queen')) return 'Requeen';
        if (lower.includes('add feeder')) return 'Add Feeders';
        if (lower.includes('remove feeder')) return 'Remove Feeders';
        if (lower.includes('pollen sub') || lower.includes('pollen patty')) return 'Add Pollen Subs';
        if (lower.includes('nutrition') || lower.includes('feed')) return 'Add Nutrition';
        if (lower.includes('beetle trap')) return 'Add Beetle Trap';
        if (lower.includes('entrance reducer')) return 'Add Entrance Reducer';
        if (lower.includes('add excluder') || lower.includes('put excluder')) return 'Add Excluder';
        if (lower.includes('remove excluder')) return 'Remove Excluder';
        if (lower.includes('add super') || lower.includes('need super') || lower.includes('another super')) return 'Add Super';
        if (lower.includes('remove super') || lower.includes('take off super')) return 'Remove Super';
        if (lower.includes('winter') || lower.includes('pack down')) return 'Pack Down for Winter';
        if (lower.includes('apiary') || lower.includes('maintenance')) return 'Apiary Maintenance Required';
        return null;
      }).filter(Boolean);
    };
    
    setFormData(prev => {
      const updated = { ...prev };
      
      // Update inspection type
      if (parsedData.inspection_type) {
        updated.inspection_type = mapInspectionType(parsedData.inspection_type);
      }
      
      // Update activity at entrance
      if (parsedData.activity_entrance) {
        updated.activity_entrance = mapActivityEntrance(parsedData.activity_entrance);
      }
      
      // Update date and time (auto-populated)
      if (parsedData.date) updated.date = parsedData.date;
      if (parsedData.time) updated.time = parsedData.time;
      
      // Update weather (auto-populated from location)
      if (parsedData.temperature) updated.temperature = parsedData.temperature;
      if (parsedData.humidity) updated.humidity = parsedData.humidity;
      if (parsedData.wind) updated.wind = parsedData.wind;
      
      // Update queen info (booleans work as-is)
      if (parsedData.queen_spotted !== undefined) {
        updated.queen_spotted = parsedData.queen_spotted;
        if (parsedData.queen_spotted) updated.no_queen = false;
      }
      if (parsedData.queen_marked !== undefined) updated.queen_marked = parsedData.queen_marked;
      if (parsedData.queen_color) {
        updated.queen_color = toTitleCase(parsedData.queen_color);
      }
      if (parsedData.queen_cells !== undefined) updated.queen_cells = parsedData.queen_cells;
      if (parsedData.no_queen !== undefined) {
        updated.no_queen = parsedData.no_queen;
        if (parsedData.no_queen) updated.queen_spotted = false;
      }
      
      // Update colony info - MAP TO FORM VALUES
      if (parsedData.population) {
        updated.population = mapPopulation(parsedData.population);
      }
      if (parsedData.colony_mood) {
        updated.colony_mood = mapColonyMood(parsedData.colony_mood);
      }
      
      // Update hive condition
      if (parsedData.hive_condition) {
        updated.hive_condition = mapHiveCondition(parsedData.hive_condition);
      }
      
      // Update brood and pattern - MAP TO FORM VALUES
      if (parsedData.brood) {
        updated.brood = mapBrood(parsedData.brood);
      }
      if (parsedData.pattern) {
        updated.pattern = mapPattern(parsedData.pattern);
      }
      
      // Update diseases and pests - MAP TO FORM VALUES
      if (parsedData.diseases) {
        updated.diseases = mapDiseases(parsedData.diseases);
      }
      if (parsedData.pests) {
        updated.pests = mapPests(parsedData.pests);
      }
      
      // Update resources - MAP TO L/M/H
      if (parsedData.nectar_level) {
        updated.nectar_level = mapLevel(parsedData.nectar_level);
      }
      if (parsedData.pollen_level) {
        updated.pollen_level = mapLevel(parsedData.pollen_level);
      }
      if (parsedData.honey_brood_level || parsedData.honey_level) {
        updated.honey_brood_level = mapLevel(parsedData.honey_brood_level || parsedData.honey_level);
      }
      
      // Update feeding
      if (parsedData.feeding) {
        const feedLower = parsedData.feeding.toLowerCase();
        if (feedLower.includes('syrup') || feedLower.includes('sugar')) {
          updated.feeding = 'syrup';
        } else if (feedLower.includes('pollen') || feedLower.includes('patty')) {
          updated.feeding = 'pollen_patty';
        } else if (feedLower.includes('fondant') || feedLower.includes('candy')) {
          updated.feeding = 'fondant';
        } else if (feedLower.includes('none') || feedLower.includes('no')) {
          updated.feeding = 'none';
        }
      }
      
      // Update mite info (booleans and numbers)
      if (parsedData.mite_wash !== undefined) updated.mite_wash = parsedData.mite_wash;
      if (parsedData.mite_count !== undefined && parsedData.mite_count !== null) {
        updated.mite_count = parsedData.mite_count.toString();
        updated.mite_percentage = calculateMitePercentage(parsedData.mite_count.toString());
      }
      
      // Update conditions (booleans)
      if (parsedData.swarm_conditions !== undefined) updated.swarm_conditions = parsedData.swarm_conditions;
      if (parsedData.foul_odour !== undefined) updated.foul_odour = parsedData.foul_odour;
      
      // Update actions taken
      if (parsedData.actions_taken) {
        const mappedActions = mapActionsTaken(parsedData.actions_taken);
        if (mappedActions.length > 0) {
          updated.actions = [...new Set([...(prev.actions || []), ...mappedActions])];
        }
      }
      
      // Update actions to take (future reminders)
      if (parsedData.actions_to_take) {
        const mappedTodoActions = mapActionsToTake(parsedData.actions_to_take);
        if (mappedTodoActions.length > 0) {
          updated.actions_to_take = [...new Set([...(prev.actions_to_take || []), ...mappedTodoActions])];
        }
      }
      
      // Add notes with transcript - always include the full transcript
      const transcriptNote = parsedData.transcript ? `[Voice Transcript]: ${parsedData.transcript}` : '';
      const aiNotes = parsedData.notes ? `[AI Summary]: ${parsedData.notes}` : '';
      const combinedNotes = [aiNotes, transcriptNote].filter(Boolean).join('\n\n');
      if (combinedNotes) {
        updated.notes = prev.notes ? `${prev.notes}\n\n${combinedNotes}` : combinedNotes;
      }
      
      return updated;
    });
    
    toast.success('Form populated from voice recording!');
  };

  const fetchHive = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [hiveRes, beekeepersRes, inspectionsRes, hivesRes, yardsRes, taskTypesRes, actionTypesRes, limitsRes] = await Promise.all([
        axios.get(`${API}/hives/${hiveId}`, { headers }),
        axios.get(`${API}/beekeepers`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/inspections`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/hives`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/yards`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/task-types`, { headers }).catch(() => ({ data: { items: [], categories: [] } })),
        axios.get(`${API}/action-types`, { headers }).catch(() => ({ data: { items: [], categories: [] } })),
        axios.get(`${API}/subscription/upload-limits`, { headers }).catch(() => ({ data: { max_photos: 5, max_videos: 0 } }))
      ]);
      setHive(hiveRes.data);
      setBeekeepers(beekeepersRes.data);
      
      // Prefill beekeeper field for crew members
      if (isCrew && user?.name && !editMode) {
        const crewName = user.name;
        const match = (beekeepersRes.data || []).find(b => b.name === crewName);
        if (match) {
          setFormData(prev => ({ ...prev, beekeeper: match.name }));
        }
      }
      setAllHives(hivesRes.data || []);
      setAllYards(yardsRes.data || []);
      setAvailableTaskTypes(taskTypesRes.data.items || []);
      setTaskCategories(taskTypesRes.data.categories || []);
      setAvailableActionTypes(actionTypesRes.data.items || []);
      if (limitsRes.data) setUploadLimits(limitsRes.data);
      setActionCategories(actionTypesRes.data.categories || []);
      
      // Fetch mite-related data
      fetchMiteData();
      
      // Auto-populate weather from latest inspection in same yard (if creating new inspection)
      if (!editMode) {
        const currentYard = hiveRes.data.yard_id;
        const allInspections = inspectionsRes.data;
        
        // Get all hives in the same yard
        const hivesInYard = await axios.get(`${API}/hives?yard_id=${currentYard}`, { headers }).catch(() => ({ data: (hivesRes.data || []).filter(h => h.yard_id === currentYard || h.yard === currentYard) }));
        const yardHiveIds = hivesInYard.data.map(h => h.id);
        
        // Find the most recent inspection from same yard (today only for weather carry-over)
        const today = new Date().toISOString().split('T')[0];
        const todayInspections = allInspections
          .filter(insp => yardHiveIds.includes(insp.hive_id) && insp.date?.split('T')[0] === today)
          .sort((a, b) => new Date(b.created_at || b.date) - new Date(a.created_at || a.date));
        
        if (todayInspections.length > 0) {
          const latestInsp = todayInspections[0];
          setFormData(prev => ({
            ...prev,
            weather: latestInsp.weather || prev.weather,
            temperature: latestInsp.temperature || prev.temperature,
            humidity: latestInsp.humidity || prev.humidity,
            wind: latestInsp.wind || prev.wind
          }));
        }
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      if (!editMode) {
        setLoading(false);
      }
    }
  };

  // Fetch available queens from breeding batches (for Requeen action)
  const fetchAvailableQueens = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/queen-rearing/batches`, { headers });
      const batches = res.data || [];
      const queens = [];
      batches.forEach(batch => {
        (batch.cells || []).forEach(cell => {
          if (['mated', 'laying', 'emerged'].includes(cell.status) && !cell.destination_hive_id) {
            queens.push({
              cell_id: cell.id,
              batch_id: batch.id,
              batch_name: batch.batch_name || `Batch #${batch.batch_number}`,
              cell_number: cell.cell_number,
              status: cell.status,
              breed: batch.queen_breed || batch.mother_queen_breed || '',
              graft_date: batch.graft_date || '',
              emerged_date: cell.emerged_date || '',
              mated_date: cell.mated_date || '',
            });
          }
        });
      });
      setAvailableQueens(queens);
    } catch (err) {
      // Queen management may not be available on the user's plan
    }
  };

  // Fetch empty/ready hives in the same yard (for Split Hive action)
  const fetchEmptyHives = () => {
    if (!hive || !allHives.length) return;
    const hivesInYard = allHives.filter(h =>
      h.yard === hive.yard && h.id !== hive.id && h.status === 'active'
    );
    setEmptyHives(hivesInYard);
  };

  
  const fetchMiteData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [typesRes, countsRes, treatmentsRes] = await Promise.all([
        axios.get(`${API}/treatment-types`, { headers }).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/mite-counts?hive_id=${hiveId}`, { headers }).catch(() => ({ data: { counts: [] } })),
        axios.get(`${API}/treatment-plans?hive_id=${hiveId}&status=active`, { headers }).catch(() => ({ data: { plans: [] } }))
      ]);
      setTreatmentTypes(typesRes.data.items || []);
      setRecentMiteCounts((countsRes.data.counts || []).slice(0, 5));
      setActiveTreatments(treatmentsRes.data.plans || []);
    } catch (err) {
    }
  };

  // Fetch hive weight data
  const fetchWeightData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [settingsRes, baselineRes, historyRes] = await Promise.all([
        axios.get(`${API}/weight-settings`, { headers }).catch(() => ({ data: { unit_preference: 'kg', alert_threshold_percent: 10 } })),
        axios.get(`${API}/hives/${hiveId}/baseline-weight`, { headers }).catch(() => ({ data: { baseline_weight_kg: 0, breakdown: [] } })),
        axios.get(`${API}/hives/${hiveId}/weight-history?limit=10`, { headers }).catch(() => ({ data: { weight_history: [] } }))
      ]);
      setWeightSettings(settingsRes.data);
      setBaselineWeight(baselineRes.data);
      setWeightHistory(historyRes.data.weight_history || []);
      
      // Auto-populate baseline weight for new inspections (not editing existing)
      if (!inspectionId && baselineRes.data && baselineRes.data.baseline_weight_kg > 0) {
        const baselineValue = settingsRes.data?.unit_preference === 'lbs' 
          ? baselineRes.data.baseline_weight_lbs 
          : baselineRes.data.baseline_weight_kg;
        setFormData(prev => ({
          ...prev,
          baseline_weight: prev.baseline_weight || baselineValue.toString()
        }));
      }
      
      // Set previous weight from most recent inspection
      const history = historyRes.data.weight_history || [];
      if (history.length > 0) {
        setPreviousWeight(parseFloat(history[0].hive_weight));
      }
    } catch (err) {
    }
  };

  useEffect(() => {
    if (hiveId && token) {
      fetchWeightData();
    }
  }, [hiveId, token]);

  // Fetch queens when Requeened action is selected
  useEffect(() => {
    if (formData.actions.includes('Requeened') && availableQueens.length === 0) {
      fetchAvailableQueens();
    }
  }, [formData.actions]);

  // Fetch empty hives when Split Hive is selected
  useEffect(() => {
    if (formData.actions.includes('Split Hive') && hive) {
      fetchEmptyHives();
    }
  }, [formData.actions, hive, allHives]);


  // Submit new action suggestion
  const handleSuggestAction = async () => {
    if (!suggestActionName.trim()) {
      toast.error('Please enter an action name');
      return;
    }
    setSubmittingSuggestion(true);
    try {
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      const response = await axios.post(`${API}/action-types`, {
        name: suggestActionName.trim(),
        category: suggestActionCategory
      }, { headers });
      toast.success(response.data.message || 'Action suggestion submitted for admin approval!');
      setSuggestActionName('');
      setSuggestActionCategory('other');
      setShowSuggestAction(false);
    } catch (err) {
      toast.error('Failed to submit suggestion');
    } finally {
      setSubmittingSuggestion(false);
    }
  };

  // Submit new task suggestion
  const handleSuggestTask = async () => {
    if (!suggestTaskName.trim()) {
      toast.error('Please enter a task name');
      return;
    }
    setSubmittingSuggestion(true);
    try {
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      const response = await axios.post(`${API}/task-types`, {
        name: suggestTaskName.trim(),
        category: suggestTaskCategory
      }, { headers });
      toast.success(response.data.message || 'Task suggestion submitted for admin approval!');
      setSuggestTaskName('');
      setSuggestTaskCategory('other');
      setShowSuggestTask(false);
    } catch (err) {
      toast.error('Failed to submit suggestion');
    } finally {
      setSubmittingSuggestion(false);
    }
  };

  const fetchInspection = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/inspections/${inspectionId}`, { headers });
      const data = response.data;
      setFormData({
        date: data.date ? new Date(data.date).toISOString().split('T')[0] : formData.date,
        beekeeper: data.beekeeper || '',
        time: data.time || formData.time,
        temperature: data.temperature || '',
        humidity: data.humidity || '',
        wind: data.wind || '',
        hive_weight: data.hive_weight || '',
        baseline_weight: data.baseline_weight || '',
        weather: data.weather || [],
        inspection_type: data.inspection_type || [],
        hive_stack: data.hive_stack || {},
        activity_entrance: data.activity_entrance || [],
        hive_condition: data.hive_condition || '',
        repairs_needed: data.repairs_needed || '',
        colony_mood: data.colony_mood || '',
        hive_orientation: data.hive_orientation || '',
        bottom_brood_box: data.bottom_brood_box || Array(10).fill(''),
        top_brood_box: data.top_brood_box || Array(10).fill(''),
        brood: data.brood || [],
        pattern: data.pattern || [],
        queen_spotted: data.queen_spotted || false,
        queen_frame: data.queen_frame || '',
        queen_marked: data.queen_marked || false,
        queen_color: data.queen_color || '',
        queen_age: data.queen_age || '',
        queen_cells: data.queen_cells || false,
        queen_cells_frames: data.queen_cells_frames || [],
        capped_cells: data.capped_cells || false,
        capped_cells_frames: data.capped_cells_frames || [],
        no_queen: data.no_queen || false,
        foul_odour: data.foul_odour || false,
        population: data.population || '',
        drone_population: data.drone_population || '',
        swarm_conditions: data.swarm_conditions || false,
        hive_condition_status: data.hive_condition_status || [],
        diseases: data.diseases || [],
        pests: data.pests || [],
        pests_other: data.pests_other || '',
        nectar_level: data.nectar_level || '',
        pollen_level: data.pollen_level || '',
        honey_brood_level: data.honey_brood_level || '',
        honey_frames: data.honey_frames || [],
        honey_frames_count: data.honey_frames_count || '',
        feeding: data.feeding || '',
        feed_type: data.feed_type || [],
        feeder_type: data.feeder_type || '',
        mite_wash: data.mite_wash || false,
        mite_method: data.mite_method || '',
        mite_count: data.mite_count || '',
        mite_percentage: data.mite_percentage || '',
        mite_treatment: data.mite_treatment || [],
        mite_application: data.mite_application || [],
        mite_dosage: data.mite_dosage || '',
        actions: data.actions || [],
        actions_other: data.actions_other || '',
        actions_to_take: data.actions_to_take || [],
        actions_assigned_beekeeper: data.actions_assigned_beekeeper || '',
        split_removed_brood_frame: data.split_removed_brood_frame || '',
        split_removed_pollen_frame: data.split_removed_pollen_frame || '',
        split_removed_honey_frame: data.split_removed_honey_frame || '',
        split_destination: data.split_destination || '',
        split_destination_hive_id: data.split_destination_hive_id || '',
        split_new_hive_yard: data.split_new_hive_yard || '',
        added_super_boxes: data.added_super_boxes || '',
        removed_super_boxes: data.removed_super_boxes || '',
        winter_pack_boxes: data.winter_pack_boxes || '',
        requeened_date: data.requeened_date || '',
        requeened_source: data.requeened_source || '',
        requeened_queen_cell_id: data.requeened_queen_cell_id || '',
        requeened_batch_id: data.requeened_batch_id || '',
        requeened_queen_breed: data.requeened_queen_breed || '',
        requeened_notes: data.requeened_notes || '',
        dead_out_cause: Array.isArray(data.dead_out_cause) ? data.dead_out_cause : (data.dead_out_cause ? [data.dead_out_cause] : []),
        dead_out_notes: data.dead_out_notes || '',
        added_brood_box_qty: data.added_brood_box_qty || '',
        removed_brood_box_qty: data.removed_brood_box_qty || '',
        added_super_qty: data.added_super_qty || '',
        removed_super_qty: data.removed_super_qty || '',
        added_frames_qty: data.added_frames_qty || '',
        removed_frames_qty: data.removed_frames_qty || '',
        // Harvest tracking
        added_full_super_qty: data.added_full_super_qty || '',
        removed_full_super_qty: data.removed_full_super_qty || '',
        added_capped_honey_frames_qty: data.added_capped_honey_frames_qty || '',
        removed_capped_honey_frames_qty: data.removed_capped_honey_frames_qty || '',
        added_nutrition: data.added_nutrition || false,
        nutrition_details: data.nutrition_details || '',
        // Combined Hives details
        combine_primary_hive: data.combine_primary_hive || '',
        combine_source_hive: data.combine_source_hive || '',
        combine_empty_hive_action: data.combine_empty_hive_action || '',
        combine_empty_hive_notes: data.combine_empty_hive_notes || '',
        // Moved Hive details
        moved_hive_type: data.moved_hive_type || '',
        moved_hive_reason: data.moved_hive_reason || '',
        moved_hive_new_position: data.moved_hive_new_position || '',
        moved_hive_new_apiary: data.moved_hive_new_apiary || '',
        moved_hive_notes: data.moved_hive_notes || '',
        notes: data.notes || '',
        photos: data.photos || [],
        videos: data.videos || [],
        // Expand frame_data dict back into individual keys for the form
        ...(data.frame_data || {}),
      });
    } catch (error) {
      toast.error('Failed to load inspection');
    } finally {
      setLoading(false);
    }
  };

  const toggleArray = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter((v) => v !== value)
        : [...prev[field], value],
    }));
  };

  const handleFileUpload = async (e, type, sectionTag) => {
    const files = Array.from(e.target.files);
    
    // Check upload limits
    const currentPhotos = (formData.photos?.length || 0) + Object.keys(offlinePhotosRef.current).length;
    const currentVideos = formData.videos?.length || 0;
    
    if (type === 'photo' && currentPhotos + files.length > uploadLimits.max_photos) {
      toast.error(`Photo limit: ${uploadLimits.max_photos} per inspection (you have ${currentPhotos})`);
      return;
    }
    if (type === 'video' && uploadLimits.max_videos === 0) {
      toast.error('Video uploads not available on your plan. Upgrade to unlock!');
      return;
    }
    if (type === 'video' && currentVideos + files.length > uploadLimits.max_videos) {
      toast.error(`Video limit: ${uploadLimits.max_videos} per inspection (you have ${currentVideos})`);
      return;
    }
    
    for (const file of files) {
      try {
        // Use chunked upload for videos larger than 5MB
        if (type === 'video' && file.size > 5 * 1024 * 1024) {
          setVideoUploadProgress({ percent: 0, status: 'Starting...' });
          const result = await uploadFileChunked(file, (progress) => {
            setVideoUploadProgress(progress);
          });
          const fileInfo = { file_id: result.path, type, name: file.name, section: sectionTag || '' };
          setUploadedFiles((prev) => [...prev, fileInfo]);
          setFormData((prev) => ({ ...prev, videos: [...prev.videos, result.path] }));
          setVideoUploadProgress(null);
          toast.success(`${sectionTag ? `${sectionTag}: ` : ''}Video uploaded (${(file.size / 1024 / 1024).toFixed(1)}MB)`);
        } else {
          // Standard single-request upload for photos and small videos
          const formDataUpload = new FormData();
          formDataUpload.append('file', file);
          const response = await axios.post(`${API}/inspections/upload-photo`, formDataUpload, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
          const fileInfo = { file_id: response.data.path, type, name: file.name, section: sectionTag || '' };
          setUploadedFiles((prev) => [...prev, fileInfo]);
          if (type === 'photo') {
            setFormData((prev) => ({ ...prev, photos: [...prev.photos, response.data.path] }));
            try {
              const blobRes = await axios.get(`${API}/files/${response.data.path}`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
              });
              const blobUrl = URL.createObjectURL(blobRes.data);
              setBlobUrls((prev) => ({ ...prev, [response.data.path]: blobUrl }));
            } catch {}
          } else {
            setFormData((prev) => ({ ...prev, videos: [...prev.videos, response.data.path] }));
          }
          toast.success(`${sectionTag ? `${sectionTag}: ` : ''}${type === 'photo' ? 'Photo' : 'Video'} uploaded`);
        }
      } catch (error) {
        setVideoUploadProgress(null);
        if (type === 'photo' && isOfflineError(error)) {
          const fileId = `offline-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
          offlinePhotosRef.current[fileId] = { blob: file, name: file.name, section: sectionTag || '' };
          setUploadedFiles((prev) => [...prev, { file_id: fileId, type, name: file.name, section: sectionTag || '', offline: true }]);
          setBlobUrls((prev) => ({ ...prev, [fileId]: URL.createObjectURL(file) }));
          toast.info('No signal: photo saved on this phone and will upload with the inspection');
          continue;
        }
        toast.error(`Failed to upload ${type}: ${error?.response?.data?.detail || error.message}`);
      }
    }
  };

  const removeFile = (fileId, type) => {
    delete offlinePhotosRef.current[fileId];
    setUploadedFiles((prev) => prev.filter((f) => f.file_id !== fileId));
    if (type === 'photo') {
      setFormData((prev) => ({ ...prev, photos: prev.photos.filter((p) => p !== fileId) }));
    } else {
      setFormData((prev) => ({ ...prev, videos: prev.videos.filter((v) => v !== fileId) }));
    }
    // Clean up blob URL
    if (blobUrls[fileId]) {
      URL.revokeObjectURL(blobUrls[fileId]);
      setBlobUrls((prev) => { const next = { ...prev }; delete next[fileId]; return next; });
    }
  };

  // Quick photo from section header camera button
  const handleSectionPhoto = (e, sectionTitle) => {
    handleFileUpload(e, 'photo', sectionTitle);
  };


  // Follow-up requests triggered by the inspection's actions (run right after save, or queued with it offline)
  const buildFollowUps = () => {
    const list = [];
    const hn = hive?.hive_number;
    if (!editMode && formData.actions.includes('Split Hive') && formData.split_destination === 'new') {
      const nextNumber = Math.max(...allHives.map(h => h.hive_number || 0), 0) + 1;
      list.push({ kind: 'split', method: 'post', path: '/hives', errorToast: 'Split saved but failed to auto-create new hive', body: {
        hive_number: nextNumber, nickname: `Split from Hive #${hn}`, yard: hive?.yard || '', hive_type: hive?.hive_type || '',
        origin_type: 'Split', is_split: true, split_from_hive: `Hive #${hn}`, split_type: 'Walk Away',
        notes: `Split from Hive #${hn} on ${new Date(formData.date).toLocaleDateString()}`, status: 'active' } });
    }
    if (!editMode && formData.actions.includes('Split Hive') && formData.split_destination === 'existing' && formData.split_destination_hive_id) {
      const destHive = allHives.find(h => h.id === formData.split_destination_hive_id);
      list.push({ kind: 'split', method: 'put', path: `/hives/${formData.split_destination_hive_id}`, body: { ...(destHive || {}),
        hive_number: destHive?.hive_number || 0, yard: destHive?.yard || '', nickname: destHive?.nickname || '',
        origin_type: 'Split', is_split: true, split_from_hive: `Hive #${hn}` } });
    }
    if (!editMode && formData.actions.includes('Requeened') && formData.requeened_source === 'queen_batch' && formData.requeened_queen_cell_id && formData.requeened_batch_id) {
      list.push({ kind: 'requeen', method: 'put', path: `/queen-rearing/batches/${formData.requeened_batch_id}/cells/${formData.requeened_queen_cell_id}`,
        body: { destination_hive_id: hiveId, destination_hive_number: String(hn || '') } });
    }
    if (formData.actions.includes('Dead Out / Lost Colony')) {
      const reason = formData.dead_out_cause.length > 0 ? formData.dead_out_cause.join(', ') : 'Dead Out';
      list.push({ kind: 'dead_out', method: 'put', path: `/hives/${hiveId}`, errorToast: 'Failed to update hive status', body: { ...(hive || {}),
        hive_number: hn || 0, yard: hive?.yard || '', nickname: hive?.nickname || '',
        status: deadOutContaminated() ? 'inactive' : 'ready', inactive_reason: `Dead Out: ${reason}` } });
      if (formData.dead_out_cause.includes('AFB (American Foulbrood)') && !editMode) {
        const yard = hive?.yard || '';
        const afbTasks = [
          { title: `🔥 URGENT: Burn all frames, boxes & equipment from Hive #${hn}`, description: `AFB detected in Hive #${hn}. ALL frames, boxes, bottom boards, lids, and any comb must be burned. Do NOT reuse any equipment. Spores survive 50+ years.`, priority: 'critical' },
          { title: `📋 Report AFB to apiary inspector / biosecurity authority`, description: `AFB is a notifiable disease. Contact your local apiary inspector or biosecurity authority to report the AFB-positive hive (Hive #${hn}, ${yard || 'Unknown yard'}).`, priority: 'critical' },
          { title: `🔍 Inspect all neighbouring hives for AFB signs`, description: `AFB from Hive #${hn} may have spread. Inspect all hives in ${yard || 'this yard'} for sunken/perforated caps, ropy larvae (matchstick test), and foul smell. Pay special attention to hives within robbing distance.`, priority: 'high' },
          { title: `🧴 Sterilise all hive tools used on Hive #${hn}`, description: `Scorching with a blow torch or soaking in bleach solution. Any tool that contacted the infected colony must be sterilised before use on other hives.`, priority: 'high' },
        ];
        afbTasks.forEach(t => list.push({ kind: 'afb_task', method: 'post', path: '/crew/tasks', body: {
          ...t, category: 'disease_management', hive_id: hiveId, hive_number: hn, yard, task_type: 'AFB Response', status: 'pending', due_date: new Date().toISOString() } }));
      }
    }
    (formData.actions_to_take || []).forEach(action => list.push({ kind: 'planned_task', method: 'post', path: '/planned-actions', body: {
      description: action, date: '', hive_id: hiveId, hive_number: hn, yard: hive?.yard, action_type: 'inspection_task',
      assignedBeekeeper: formData.actions_assigned_beekeeper || '', notes: `From inspection on ${new Date(formData.date).toLocaleDateString()}` } }));
    return list;
  };

  const deadOutContaminated = () => {
    const contaminatedCauses = ['AFB (American Foulbrood)', 'EFB (European Foulbrood)', 'Pesticide Exposure', 'Nosema', 'Unknown'];
    return formData.dead_out_cause.some(c => contaminatedCauses.includes(c));
  };

  const followUpSummary = () => {
    const kinds = buildFollowUps().map(f => f.kind);
    const names = { split: 'split', requeen: 'requeen', dead_out: 'dead-out status', afb_task: 'AFB tasks', planned_task: 'planned tasks' };
    return [...new Set(kinds)].map(k => names[k]).join(', ').replace(/^./, c => c.toUpperCase());
  };

  const announceFollowUps = () => {
    const kinds = buildFollowUps().map(f => f.kind);
    const hn = hive?.hive_number;
    if (kinds.includes('split') && formData.split_destination === 'new') toast.success(`New Hive #${Math.max(...allHives.map(h => h.hive_number || 0), 0) + 1} created from split`);
    if (kinds.includes('dead_out')) {
      const reason = formData.dead_out_cause.length > 0 ? formData.dead_out_cause.join(', ') : 'Dead Out';
      if (deadOutContaminated()) toast.error(`Hive #${hn} marked Inactive — ${reason}. Equipment may be contaminated.`, { duration: 6000 });
      else toast.info(`Hive #${hn} marked Ready (Prepared) — ${reason}. Equipment can be reused.`, { duration: 6000 });
    }
    if (kinds.includes('afb_task')) toast.warning('4 critical AFB tasks created — check your Tasks list', { duration: 8000 });
    const planned = (formData.actions_to_take || []).length;
    if (planned) {
      if (formData.actions_assigned_beekeeper) toast.success(`${planned} task(s) assigned to ${formData.actions_assigned_beekeeper}`);
      else toast.info(`${planned} task(s) added - please schedule dates in Tasks view`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    let pendingSubmit = null;
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Collect dynamic frame data (box_frames_*, legacy_box_*) into frame_data dict
      const frameData = {};
      const submitData = {};
      Object.entries(formData).forEach(([key, value]) => {
        if (key.startsWith('box_frames_') || key.startsWith('legacy_box_')) {
          frameData[key] = value;
        } else {
          submitData[key] = value;
        }
      });
      submitData.frame_data = frameData;
      submitData.hive_id = hiveId;
      submitData.date = new Date(formData.date).toISOString();
      pendingSubmit = submitData;

      // Upload any photos captured while offline (throws offline error if still no signal)
      for (const [fileId, p] of Object.entries(offlinePhotosRef.current)) {
        const fd = new FormData();
        fd.append('file', p.blob, p.name || 'photo.jpg');
        const up = await axios.post(`${API}/inspections/upload-photo`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
        submitData.photos = [...(submitData.photos || []), up.data.path];
        delete offlinePhotosRef.current[fileId];
      }
      
      if (editMode && inspectionId) {
        await axios.put(`${API}/inspections/${inspectionId}`, submitData, { headers });
        toast.success('Inspection updated successfully');
      } else {
        const wasReady = hive?.status === 'ready';
        await axios.post(`${API}/inspections`, submitData, { headers });
        toast.success('Inspection saved successfully');
        
        // Alert beekeeper that the hive was auto-activated and prompt to complete Edit Hive
        if (wasReady) {
          toast.info('Hive status changed from Ready to Active', {
            description: 'Please review Edit Hive to complete hive details (bee variety, queen info, etc.)',
            duration: 8000,
            action: {
              label: 'Edit Hive',
              onClick: () => navigate(`/?view=hive-detail&hiveId=${hiveId}`),
            },
          });
        } else if (!editMode) {
          // Gentle nudge if hive is missing key details
          const missingFields = [];
          if (!hive?.bee_variety) missingFields.push('bee variety');
          if (!hive?.queen_source && !hive?.queen_genetics) missingFields.push('queen info');
          if (!hive?.origin_type) missingFields.push('origin');
          if (missingFields.length >= 2) {
            toast('Tip: Update your hive details', {
              description: `Missing: ${missingFields.join(', ')}. Complete these in Edit Hive for richer reports.`,
              duration: 6000,
              action: {
                label: 'Edit Hive',
                onClick: () => navigate(`/?view=hive-detail&hiveId=${hiveId}`),
              },
            });
          }
        }
        
        // Show additional message for crew that task was auto-completed
        if (isCrew) {
          toast.success('Task marked as complete!', {
            description: 'Your inspection task has been automatically completed.',
            duration: 4000,
          });
        }
      }

      const followUps = buildFollowUps();
      for (const f of followUps) {
        try { await axios({ method: f.method, url: `${API}${f.path}`, data: f.body, headers }); }
        catch (err) { if (f.errorToast) toast.error(f.errorToast); }
      }
      announceFollowUps();
      
      // Navigate back - crew goes to dashboard, owner goes to hive detail
      if (isCrew) {
        navigate('/crew-dashboard');
      } else {
        navigate(`/?view=hive-detail&hiveId=${hiveId}`);
      }
    } catch (error) {
      if (pendingSubmit && isOfflineError(error)) {
        await enqueue({
          kind: 'inspection',
          method: editMode && inspectionId ? 'put' : 'post',
          path: editMode && inspectionId ? `/inspections/${inspectionId}` : '/inspections',
          body: pendingSubmit,
          photos: Object.values(offlinePhotosRef.current),
          label: `Inspection · Hive ${hive?.hive_number || ''}`,
          hiveIds: [hiveId],
          followUps: buildFollowUps()
        });
        offlinePhotosRef.current = {};
        const queuedFollowUps = buildFollowUps();
        toast.success('No signal: inspection saved on this phone', {
          description: queuedFollowUps.length
            ? `${followUpSummary()} will also be applied automatically when you are back in coverage.`
            : 'It will upload automatically when you are back in coverage.',
          duration: 8000
        });
        navigate(isCrew ? '/crew-dashboard' : '/?view=hives');
        return;
      }
      toast.error(editMode ? 'Failed to update inspection' : 'Failed to save inspection');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center">
        <div className="text-stone-600 text-lg">Loading...</div>
      </div>
    );
  }

  // Determine if this is a quick/external inspection that should collapse detailed sections
  const isQuickInspection = formData.inspection_type?.includes('External') || 
                            formData.inspection_type?.includes('Superficial');

  return (
    <div className="bg-stone-50 pb-32">
      <div className="bg-white border-b border-stone-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-2xl mx-auto p-2 md:p-4">
          {/* Mobile Layout - Stacked */}
          <div className="flex md:hidden flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(isCrew ? '/crew-dashboard' : '/')}
                  data-testid="home-btn-mobile"
                  className="h-10 w-10"
                  title="Home"
                >
                  <Home className="h-5 w-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(isCrew ? '/crew-dashboard' : `/?view=hive-detail&hiveId=${hiveId}`)}
                  data-testid="back-btn-mobile"
                  className="h-10 w-10"
                  title={isCrew ? "Back to Dashboard" : "Back to Hive"}
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </div>
              <div className="flex gap-2">
                {!editMode && (
                  <Button
                    type="button"
                    onClick={() => setShowVoiceDialog(true)}
                    data-testid="voice-inspection-btn-mobile"
                    className="bg-purple-600 hover:bg-purple-700 text-white rounded-full h-10 w-10 p-0"
                    title="Voice Inspection"
                  >
                    <Mic className="h-5 w-5" />
                  </Button>
                )}
                <Button
                  onClick={handleSubmit}
                  disabled={saving}
                  data-testid="save-inspection-btn-mobile"
                  className="bg-amber-600 hover:bg-amber-700 text-white rounded-full h-10 px-4 font-bold"
                >
                  <Save className="h-4 w-4 mr-1" />
                  {saving ? '...' : 'Save'}
                </Button>
              </div>
            </div>
            <h1 className="text-base font-bold text-stone-800 font-heading text-center truncate px-2">
              {editMode ? 'Edit' : 'New'} Inspection - Hive {hive?.hive_number}
            </h1>
          </div>
          
          {/* Desktop Layout - Single Row */}
          <div className="hidden md:flex items-center justify-between">
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(isCrew ? '/crew-dashboard' : '/')}
                data-testid="home-btn"
                className="h-12 w-12"
                title="Home"
              >
                <Home className="h-6 w-6" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(isCrew ? '/crew-dashboard' : `/?view=hive-detail&hiveId=${hiveId}`)}
                data-testid="back-btn"
                className="h-12 w-12"
                title={isCrew ? "Back to Dashboard" : "Back to Hive"}
              >
                <ArrowLeft className="h-6 w-6" />
              </Button>
            </div>
            <h1 className="text-xl font-bold text-stone-800 font-heading">
              {editMode ? 'Edit' : 'New'} Inspection - Hive {hive?.hive_number}
            </h1>
            <div className="flex gap-2">
              {!editMode && (
                <Button
                  type="button"
                  onClick={() => setShowVoiceDialog(true)}
                  data-testid="voice-inspection-btn"
                  className="bg-purple-600 hover:bg-purple-700 text-white rounded-full h-12 px-4 font-bold"
                  title="Voice Inspection - Record and auto-fill form"
                >
                  <Mic className="h-5 w-5 mr-2" />
                  Voice
                </Button>
              )}
              <Button
                onClick={handleSubmit}
                disabled={saving}
                data-testid="save-inspection-btn"
                className="bg-amber-600 hover:bg-amber-700 text-white rounded-full h-12 px-6 font-bold"
              >
                <Save className="mr-2 h-5 w-5" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-2xl mx-auto p-4 space-y-8">
        <HeaderSection
          formData={formData} setFormData={setFormData} toggleArray={toggleArray}
          beekeepers={beekeepers} weatherLoading={weatherLoading} weatherAutoFetched={weatherAutoFetched}
        />

        {/* Hive Weight Section */}
        <WeightSection
          formData={formData} setFormData={setFormData} handleSectionPhoto={handleSectionPhoto}
          weightSettings={weightSettings} setWeightSettings={setWeightSettings} baselineWeight={baselineWeight}
          previousWeight={previousWeight} weightHistory={weightHistory}
        />

        <OverviewSection
          formData={formData} setFormData={setFormData} toggleArray={toggleArray}
          handleSectionPhoto={handleSectionPhoto}
        />
        <FrameByFrameSection
          formData={formData} setFormData={setFormData} toggleArray={toggleArray}
          handleSectionPhoto={handleSectionPhoto} isQuickInspection={isQuickInspection}
          hive={hive} DynamicFrameSelectors={DynamicFrameSelectors}
        />

        <ConditionSection
          formData={formData} setFormData={setFormData} toggleArray={toggleArray}
          isQuickInspection={isQuickInspection} handleSectionPhoto={handleSectionPhoto}
        />

        <ResourcesSection
          formData={formData} setFormData={setFormData} toggleArray={toggleArray}
          isQuickInspection={isQuickInspection} handleSectionPhoto={handleSectionPhoto}
        />

        <MiteSection
          formData={formData} isQuickInspection={isQuickInspection} handleSectionPhoto={handleSectionPhoto}
          hive={hive} treatmentJustStarted={treatmentJustStarted}
          activeTreatments={activeTreatments} recentMiteCounts={recentMiteCounts}
          setShowFrameScanDialog={setShowFrameScanDialog} setShowMiteCountDialog={setShowMiteCountDialog}
          setShowTreatmentWizard={setShowTreatmentWizard}
          token={token}
        />

        <ActionsSection
          formData={formData} setFormData={setFormData} toggleArray={toggleArray}
          handleSectionPhoto={handleSectionPhoto}
          actionCategories={actionCategories} availableActionTypes={availableActionTypes}
          hive={hive} allHives={allHives} allYards={allYards}
          availableQueens={availableQueens}
          showSuggestAction={showSuggestAction} setShowSuggestAction={setShowSuggestAction}
          suggestActionName={suggestActionName} setSuggestActionName={setSuggestActionName}
          suggestActionCategory={suggestActionCategory} setSuggestActionCategory={setSuggestActionCategory}
          handleSuggestAction={handleSuggestAction} submittingSuggestion={submittingSuggestion}
        />

        {/* Assign Tasks (Future Reminders) */}
        <TasksSection
          formData={formData} setFormData={setFormData} toggleArray={toggleArray} handleSectionPhoto={handleSectionPhoto}
          taskCategories={taskCategories} availableTaskTypes={availableTaskTypes} beekeepers={beekeepers}
          showSuggestTask={showSuggestTask} setShowSuggestTask={setShowSuggestTask}
          suggestTaskName={suggestTaskName} setSuggestTaskName={setSuggestTaskName}
          suggestTaskCategory={suggestTaskCategory} setSuggestTaskCategory={setSuggestTaskCategory}
          handleSuggestTask={handleSuggestTask} submittingSuggestion={submittingSuggestion}
        />

        <PhotosVideosSection
          formData={formData} uploadLimits={uploadLimits} uploadedFiles={uploadedFiles}
          blobUrls={blobUrls} videoUploadProgress={videoUploadProgress}
          handleFileUpload={handleFileUpload} removeFile={removeFile}
        />

        <Section title="Additional Notes">
          <Textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            data-testid="notes-input"
            placeholder="Add any additional observations, concerns, or notes..."
            className="min-h-32"
          />
        </Section>

        <div className="sticky bottom-12 sm:bottom-0 bg-white border-t border-stone-200 p-3 sm:p-4 -mx-4 flex gap-2 sm:gap-3 z-40">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate(isCrew ? '/crew-dashboard' : `/?view=hive-detail&hiveId=${hiveId}`)}
            data-testid="cancel-btn"
            className="flex-1 h-12 sm:h-16 rounded-full font-semibold text-sm sm:text-lg"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={saving}
            data-testid="submit-inspection-btn"
            className="flex-1 bg-amber-600 hover:bg-amber-700 text-white h-12 sm:h-16 rounded-full font-bold text-sm sm:text-lg"
          >
            <Save className="mr-1 sm:mr-2 h-4 w-4 sm:h-6 sm:w-6" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </form>

      {/* Voice Inspection Dialog */}
      {!editMode && (
        <VoiceInspection
          open={showVoiceDialog}
          onClose={() => setShowVoiceDialog(false)}
          onDataParsed={handleVoiceDataParsed}
          token={token}
          userLocation={userLocation}
        />
      )}

      {/* AI Frame Scanner Dialog */}
      <FrameScanDialog
        open={showFrameScanDialog}
        onClose={() => setShowFrameScanDialog(false)}
        hives={allHives}
        token={token}
        inspectionId={inspectionId || ''}
        onScanComplete={(result) => {
          toast.success('Frame analyzed!');
          // Update form with scan results
          if (result.brood_pattern) {
            const patternMapping = {
              'excellent': 'Solid',
              'good': 'Good',
              'spotty': 'Spotty',
              'poor': 'Poor'
            };
            const mappedPattern = patternMapping[result.brood_pattern];
            if (mappedPattern && !formData.pattern?.includes(mappedPattern)) {
              setFormData(prev => ({
                ...prev,
                pattern: [...(prev.pattern || []), mappedPattern]
              }));
            }
          }
          if (result.diseases_detected?.length > 0) {
            setFormData(prev => ({
              ...prev,
              diseases: [...new Set([...(prev.diseases || []), ...result.diseases_detected])]
            }));
          }
          if (result.mite_visibility === 'high' || result.mite_visibility === 'moderate') {
            setFormData(prev => ({
              ...prev,
              mites_detected: true
            }));
          }
          if (result.queen_cells_detected) {
            setFormData(prev => ({
              ...prev,
              queen_cells: true
            }));
          }
          fetchMiteData();
        }}
        onStartTreatment={(context) => {
          setShowFrameScanDialog(false);
          setTreatmentContext(context);
          setShowTreatmentWizard(true);
        }}
      />

      {/* Treatment Plan Wizard */}
      <TreatmentPlanWizard
        open={showTreatmentWizard}
        onClose={() => {
          setShowTreatmentWizard(false);
          setTreatmentContext(null);
        }}
        onSuccess={(result) => {
          fetchMiteData();
          setTreatmentContext(null);
          setTreatmentJustStarted(result?.treatment_type_name || 'Treatment');
          // Auto-fill the mite_treatment if a treatment was just started
          if (result?.treatment_type_name) {
            setFormData(prev => ({
              ...prev,
              mite_treatment: [result.treatment_type_name]
            }));
          }
          toast.success('Treatment plan created!');
        }}
        hives={allHives}
        yards={allYards}
        token={token}
        initialContext={treatmentContext || { hive: hive }}
      />

      {/* Mite Count Dialog */}
      <MiteCountDialog
        open={showMiteCountDialog}
        onClose={() => setShowMiteCountDialog(false)}
        hives={allHives}
        token={token}
        preselectedHiveId={hiveId}
        onSuccess={() => {
          fetchMiteData();
          toast.success('Mite count recorded!');
        }}
      />
    </div>
  );
};

const FrameSelector = ({ frames, onChange, testPrefix }) => {
  // Options for cycling through - order matters for touch cycling
  const options = ['', 'F', 'C', 'B', 'D', 'P', 'N', 'H', 'BB'];
  
  // Cycle to next option when tapping a frame (one-hand friendly)
  const handleFrameTap = (index) => {
    const currentValue = frames[index] || '';
    const currentIdx = options.indexOf(currentValue);
    const nextIdx = (currentIdx + 1) % options.length;
    const newFrames = [...frames];
    newFrames[index] = options[nextIdx];
    onChange(newFrames);
  };

  const getFrameColor = (value) => {
    const colorMap = {
      'F': 'bg-stone-200 border-stone-400 text-stone-700',
      'C': 'bg-yellow-100 border-yellow-400 text-yellow-800',
      'B': 'bg-amber-200 border-amber-500 text-amber-800',
      'D': 'bg-blue-200 border-blue-400 text-blue-800',
      'P': 'bg-orange-200 border-orange-400 text-orange-800',
      'N': 'bg-green-100 border-green-400 text-green-800',
      'H': 'bg-amber-300 border-amber-600 text-amber-900',
      'BB': 'bg-yellow-300 border-yellow-600 text-yellow-900'
    };
    return colorMap[value] || 'bg-white border-stone-300 text-stone-400';
  };

  const getOptionName = (code) => {
    const names = {
      'F': 'Foundation', 'C': 'Comb', 'B': 'Brood', 'D': 'Drone',
      'P': 'Pollen', 'N': 'Nectar', 'H': 'Honey', 'BB': 'Bee Bread'
    };
    return names[code] || '';
  };

  return (
    <div className="space-y-2">
      <p className="text-xs text-stone-500 mb-2">👆 Tap each frame to cycle through: Empty → F → C → B → D → P → N → H → BB</p>
      <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
        {frames.map((value, index) => (
          <button
            key={index}
            type="button"
            onClick={() => handleFrameTap(index)}
            data-testid={`${testPrefix}-frame-${index + 1}`}
            className={`h-20 rounded-xl border-2 transition-all font-bold text-lg active:scale-95 ${
              getFrameColor(value)
            } hover:scale-105 touch-manipulation`}
            title={value ? `Frame ${index + 1}: ${getOptionName(value)} - Tap to change` : `Frame ${index + 1}: Empty - Tap to set`}
          >
            <div className="text-xs text-stone-500 mb-1">{index + 1}</div>
            <div className={value ? '' : 'text-stone-400'}>{value || '-'}</div>
          </button>
        ))}
      </div>
    </div>
  );
};

// Dynamic Frame Selectors Component - renders based on hive configuration
const DynamicFrameSelectors = ({ hive, formData, setFormData }) => {
  const stack = hive?.hive_stack || {};
  const hiveType = (stack.hive_type || '').toLowerCase();
  
  // Initialize frame arrays based on size
  const getOrCreateFrameArray = (key, size) => {
    const frameCount = parseInt(size) || 10;
    if (!formData[key] || formData[key].length !== frameCount) {
      return Array(frameCount).fill('');
    }
    return formData[key];
  };

  // Bulk fill helper for super boxes
  const SuperBulkFill = ({ frameKey, frameCount }) => {
    const bulkOptions = [
      { label: 'All Capped', value: 'H', bg: 'bg-amber-100 border-amber-400 text-amber-800 hover:bg-amber-200' },
      { label: 'All Nectar', value: 'N', bg: 'bg-green-100 border-green-400 text-green-800 hover:bg-green-200' },
      { label: 'All Drawn', value: 'C', bg: 'bg-yellow-100 border-yellow-400 text-yellow-800 hover:bg-yellow-200' },
      { label: 'All Foundation', value: 'F', bg: 'bg-stone-100 border-stone-400 text-stone-700 hover:bg-stone-200' },
      { label: 'Clear', value: '', bg: 'bg-white border-stone-300 text-stone-500 hover:bg-stone-50' },
    ];
    return (
      <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`bulk-fill-${frameKey}`}>
        <span className="text-xs text-blue-600 font-medium self-center mr-1">Quick fill:</span>
        {bulkOptions.map(opt => (
          <button
            key={opt.label}
            type="button"
            onClick={() => {
              const filled = Array(parseInt(frameCount) || 10).fill(opt.value);
              setFormData(prev => ({ ...prev, [frameKey]: filled }));
            }}
            className={`px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${opt.bg}`}
            data-testid={`bulk-${opt.label.toLowerCase().replace(/\s/g, '-')}-${frameKey}`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    );
  };

  // ===== HORIZONTAL HIVE =====
  if (hiveType === 'horizontal' || hiveType.includes('top bar') || hiveType.includes('long')) {
    const frameCount = parseInt(stack.horizontal_hive_frames) || 20;
    const entrances = stack.horizontal_hive_entrances || '1';
    const colonies = stack.horizontal_hive_colonies || '1';
    const frames = getOrCreateFrameArray('horizontal_frames', frameCount);
    
    return (
      <div className="space-y-6">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
          <span className="font-medium">🐝 Horizontal Hive:</span> {frameCount} frames • {entrances} entrance{entrances !== '1' ? 's' : ''} • {colonies} colon{colonies !== '1' ? 'ies' : 'y'}
        </div>
        <FormField label={`Horizontal Hive (${frameCount} frames, left to right)`}>
          <FrameSelector
            frames={frames}
            onChange={(newFrames) => setFormData(prev => ({ ...prev, horizontal_frames: newFrames }))}
            testPrefix="horizontal"
          />
        </FormField>
      </div>
    );
  }

  // ===== FLOW HIVE =====
  if (hiveType.includes('flow')) {
    const broodFrames = parseInt(stack.flow_hive_deep_brood_size) || 8;
    const broodQty = parseInt(stack.flow_hive_deep_brood_qty) || 1;
    const superFrames = parseInt(stack.flow_hive_super_size) || 6;
    const superQty = parseInt(stack.flow_hive_super_qty) || 0;
    const broodBoxFrames = getOrCreateFrameArray('flow_brood_frames', broodFrames);
    const flowSuperFrames = getOrCreateFrameArray('flow_super_frames', superFrames);
    
    // Build description based on what's configured
    const configParts = [];
    if (broodQty > 0) configParts.push(`Brood Box (${broodFrames} frames)`);
    if (superQty > 0) configParts.push(`Flow Super (${superFrames} frames)`);
    const configDesc = configParts.join(' → ') || 'No boxes configured';
    
    return (
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
          <span className="font-medium">🍯 Flow Hive:</span> {configDesc}
        </div>
        {broodQty > 0 && (
          <FormField label={`Brood Box (${broodFrames} frames)`}>
            <FrameSelector
              frames={broodBoxFrames}
              onChange={(newFrames) => setFormData(prev => ({ ...prev, flow_brood_frames: newFrames }))}
              testPrefix="flow-brood"
            />
          </FormField>
        )}
        {superQty > 0 && (
          <FormField label={`Flow Super (${superFrames} frames)`}>
            <SuperBulkFill frameKey="flow_super_frames" frameCount={superFrames} />
            <FrameSelector
              frames={flowSuperFrames}
              onChange={(newFrames) => setFormData(prev => ({ ...prev, flow_super_frames: newFrames }))}
              testPrefix="flow-super"
            />
          </FormField>
        )}
        {broodQty === 0 && superQty === 0 && (
          <div className="bg-amber-100 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            <span className="font-medium">⚠️</span> No boxes configured for this Flow Hive. Update hive settings to add boxes.
          </div>
        )}
      </div>
    );
  }

  // ===== STACKABLE HIVES WITH BOX CONFIG =====
  const boxConfig = hive?.box_config || stack.box_config;
  
  // Also check equipment_config for actual counts (updated by previous inspections)
  const eqConfig = hive?.equipment_config || {};
  
  // Determine effective box counts: use equipment_config if available, else box_config
  const getEffectiveBoxConfig = () => {
    if (boxConfig && typeof boxConfig === 'object' && Object.keys(boxConfig).length > 0) {
      // Clone box_config and override qty with equipment_config counts where available
      const effective = {};
      Object.entries(boxConfig).forEach(([boxName, config]) => {
        const lower = boxName.toLowerCase();
        let eqQty = null;
        if (lower.includes('brood')) {
          if (lower.includes('10') || config.size === '10' || config.size === 10) eqQty = eqConfig.brood_10f_count;
          else if (lower.includes('8') || config.size === '8' || config.size === 8) eqQty = eqConfig.brood_8f_count;
        } else if (lower.includes('ideal')) {
          eqQty = eqConfig.super_10f_ideal_count ?? eqConfig.super_8f_ideal_count;
        } else if (lower.includes('deep') && lower.includes('super')) {
          eqQty = eqConfig.super_10f_deep_count ?? eqConfig.super_8f_deep_count;
        }
        effective[boxName] = { ...config, qty: (eqQty != null && eqQty > 0) ? eqQty : (config.qty || 1) };
      });
      return effective;
    }
    // Build box_config from equipment_config if no box_config exists
    // Only include types that match the hive's actual hive_stack setup
    if (Object.keys(eqConfig).length > 0) {
      const built = {};
      const broodSize = stack.deep_brood_size || '10';
      const superSize = stack.deep_super_size || '10';
      const idealSize = stack.ideal_super_size || '10';
      
      // Only add brood boxes matching the hive's configured size
      if (broodSize === '10' && eqConfig.brood_10f_count > 0) built['Deep Brood'] = { qty: eqConfig.brood_10f_count, size: '10' };
      else if (broodSize === '8' && eqConfig.brood_8f_count > 0) built['Deep Brood'] = { qty: eqConfig.brood_8f_count, size: '8' };
      
      // Only add super types the hive is configured for
      if ((stack.deep_super_qty > 0 || eqConfig.super_10f_deep_count > 0) && superSize === '10' && eqConfig.super_10f_deep_count > 0) 
        built['Deep Super'] = { qty: eqConfig.super_10f_deep_count, size: '10' };
      if ((stack.deep_super_qty > 0 || eqConfig.super_8f_deep_count > 0) && superSize === '8' && eqConfig.super_8f_deep_count > 0) 
        built['Deep Super'] = { qty: eqConfig.super_8f_deep_count, size: '8' };
      if ((stack.ideal_super_qty > 0 || eqConfig.super_10f_ideal_count > 0) && eqConfig.super_10f_ideal_count > 0) 
        built['Ideal Super'] = { qty: eqConfig.super_10f_ideal_count, size: idealSize };
      
      if (Object.keys(built).length > 0) return built;
    }
    return null;
  };
  
  const effectiveBoxConfig = getEffectiveBoxConfig();
  if (effectiveBoxConfig && Object.keys(effectiveBoxConfig).length > 0) {
    const boxes = [];
    Object.entries(effectiveBoxConfig).forEach(([boxName, config]) => {
      const qty = config.qty || 1;
      const size = config.size || '10';
      for (let i = 0; i < qty; i++) {
        boxes.push({
          key: `${boxName.toLowerCase().replace(/\s+/g, '_')}_${i + 1}`,
          label: qty > 1 ? `${boxName} ${i + 1}` : boxName,
          size: parseInt(size) || 10,
          boxType: boxName.toLowerCase().includes('brood') ? 'brood' : 'super'
        });
      }
    });
    
    // Sort: brood boxes first (bottom), then supers (top)
    boxes.sort((a, b) => {
      if (a.boxType === 'brood' && b.boxType === 'super') return -1;
      if (a.boxType === 'super' && b.boxType === 'brood') return 1;
      return 0;
    });
    
    const hiveTypeName = stack.hive_type || 'Stackable Hive';
    
    return (
      <div className="space-y-6">
        <div className="bg-stone-100 rounded-lg p-3 text-sm text-stone-600">
          <span className="font-medium">📦 {hiveTypeName}:</span> {boxes.map(b => b.label).join(' → ')} (bottom to top)
        </div>
        {boxes.map((box) => {
          const frameKey = `box_frames_${box.key}`;
          const frames = getOrCreateFrameArray(frameKey, box.size);
          return (
            <FormField key={box.key} label={`${box.label} (${box.size} frames)`}>
              {box.boxType === 'super' && <SuperBulkFill frameKey={frameKey} frameCount={box.size} />}
              <FrameSelector
                frames={frames}
                onChange={(newFrames) => setFormData(prev => ({ ...prev, [frameKey]: newFrames }))}
                testPrefix={box.key}
              />
            </FormField>
          );
        })}
      </div>
    );
  }

  // ===== LEGACY LANGSTROTH FORMAT =====
  const legacyBoxes = [];
  if (stack.deep_brood_qty) {
    for (let i = 0; i < parseInt(stack.deep_brood_qty); i++) {
      legacyBoxes.push({ name: `Deep Brood ${i + 1}`, size: stack.deep_brood_size || '10', type: 'brood' });
    }
  }
  if (stack.medium_brood_qty) {
    for (let i = 0; i < parseInt(stack.medium_brood_qty); i++) {
      legacyBoxes.push({ name: `Medium Brood ${i + 1}`, size: stack.medium_brood_size || '8', type: 'brood' });
    }
  }
  if (stack.deep_super_qty) {
    for (let i = 0; i < parseInt(stack.deep_super_qty); i++) {
      legacyBoxes.push({ name: `Deep Super ${i + 1}`, size: stack.deep_super_size || '10', type: 'super' });
    }
  }
  if (stack.medium_super_qty) {
    for (let i = 0; i < parseInt(stack.medium_super_qty); i++) {
      legacyBoxes.push({ name: `Medium Super ${i + 1}`, size: stack.medium_super_size || '8', type: 'super' });
    }
  }
  if (stack.ideal_super_qty) {
    for (let i = 0; i < parseInt(stack.ideal_super_qty); i++) {
      legacyBoxes.push({ name: `Ideal Super ${i + 1}`, size: stack.ideal_super_size || '10', type: 'super' });
    }
  }
  if (stack.shallow_super_qty) {
    for (let i = 0; i < parseInt(stack.shallow_super_qty); i++) {
      legacyBoxes.push({ name: `Shallow Super ${i + 1}`, size: stack.shallow_super_size || '10', type: 'super' });
    }
  }
  
  if (legacyBoxes.length > 0) {
    return (
      <div className="space-y-6">
        <div className="bg-stone-100 rounded-lg p-3 text-sm text-stone-600">
          <span className="font-medium">📦 Langstroth:</span> {legacyBoxes.map(b => b.name).join(' → ')} (bottom to top)
        </div>
        {legacyBoxes.map((box, idx) => {
          const frameKey = `legacy_box_${idx}`;
          const frames = getOrCreateFrameArray(frameKey, box.size);
          return (
            <FormField key={idx} label={`${box.name} (${box.size} frames)`}>
              {box.type === 'super' && <SuperBulkFill frameKey={frameKey} frameCount={box.size} />}
              <FrameSelector
                frames={frames}
                onChange={(newFrames) => setFormData(prev => ({ ...prev, [frameKey]: newFrames }))}
                testPrefix={`legacy-${idx}`}
              />
            </FormField>
          );
        })}
      </div>
    );
  }
  
  // ===== FALLBACK: Default 2-box layout =====
  return (
    <div className="space-y-6">
      <div className="bg-amber-100 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
        <span className="font-medium">⚠️ Note:</span> No box configuration found for this hive. Showing default layout. 
        <br/><span className="text-xs">Configure your hive stack in the Hive Details to see the correct frame layout.</span>
      </div>
      <FormField label="Bottom Brood Box (Frames 1-10)">
        <FrameSelector
          frames={formData.bottom_brood_box || Array(10).fill('')}
          onChange={(frames) => setFormData(prev => ({ ...prev, bottom_brood_box: frames }))}
          testPrefix="bottom"
        />
      </FormField>
      <FormField label="Top Brood Box (Frames 1-10)">
        <FrameSelector
          frames={formData.top_brood_box || Array(10).fill('')}
          onChange={(frames) => setFormData(prev => ({ ...prev, top_brood_box: frames }))}
          testPrefix="top"
        />
      </FormField>
    </div>
  );
};

export default InspectionForm;
